import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { toast } from 'sonner';
import { ADRESSE_VIDE, adresseEnUneLigne, adresseRenseignee, corpsAdresse, verifierAdresse, type AdresseSaisie } from '../utils/adresseClient';

interface Customer {
  id: string;
  email: string;
  phone: string | null;
  first_name: string;
  last_name: string;
  postal_address: string | null;
  vat_number: string | null;
  active: boolean;
  email_verified: boolean;
  /** Autorisé à régler sa commande en ligne au comptoir (19/10/2026). */
  paiement_au_retrait?: boolean;
  /** Déjà client du magasin / nouveau venu par la boutique en ligne / pas encore dit (28/10/2026). */
  client_magasin?: boolean | null;
  created_at: string;
  wallet?: { balance_cents: number };
  wallet_balance_cents?: number;
  orders?: any[];
  addresses?: any[];
}

interface CustomerDetail extends Customer {
  preferences?: {
    notes: string | null;
    dietary_restrictions: any;
  };
  wallet?: { id: string; balance_cents: number; currency: string };
  orders?: any[];
  addresses?: any[];
}

const PAGE_SIZE = 30;

function formatPrice(cents: number): string {
  return (cents / 100).toFixed(2) + ' \u20ac';
}

/**
 * Adresse technique posée par le serveur à un client créé SANS email
 * (crèche, CPAS, client de comptoir) : non routable, rien n'y part jamais.
 * On ne l'affiche pas — ce n'est pas l'email du client.
 */
const estEmailTechnique = (email?: string | null) => !!email && /@sans-email\.invalid$/i.test(email);

export default function Customers() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Detail panel
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Create/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [formData, setFormData] = useState({ first_name: '', last_name: '', email: '', phone: '', postal_address: '', vat_number: '' });
  const [saving, setSaving] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  // Création : rue, code postal, ville → une vraie adresse (facture ET livraison).
  const [adresse, setAdresse] = useState<AdresseSaisie>(ADRESSE_VIDE);
  // En modification : l'adresse (structurée) qu'on édite, si le client en a une.
  const [adresseId, setAdresseId] = useState<string | null>(null);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      if (search.trim().length >= 2) {
        const results = await api.searchCustomers(search.trim(), 50);
        setCustomers(Array.isArray(results) ? results : []);
        setTotal(Array.isArray(results) ? results.length : 0);
      } else {
        const result = await api.listCustomers(page, PAGE_SIZE);
        setCustomers(result.data || []);
        setTotal(result.meta?.total || result.total || 0);
      }
    } catch (err) {
      console.error('Erreur chargement clients:', err);
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    const timer = setTimeout(() => loadCustomers(), search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [loadCustomers]);

  const loadCustomerDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const detail = await api.getCustomer(id);
      setSelectedCustomer(detail);
    } catch (err) {
      console.error('Erreur chargement détail client:', err);
      toast.error('Erreur lors du chargement du client');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCustomer(null);
    setFormData({ first_name: '', last_name: '', email: '', phone: '', postal_address: '', vat_number: '' });
    setAdresse(ADRESSE_VIDE);
    setAdresseId(null);
    setTempPassword(null);
    setShowModal(true);
  };

  /**
   * La fenêtre part de la FICHE COMPLÈTE et de ses adresses, jamais de la ligne
   * de la liste : celle-ci n'avait ni adresse ni TVA, la fenêtre s'ouvrait vide
   * et l'enregistrement les effaçait (13/10/2026).
   */
  const handleEdit = async (customer: Customer) => {
    let fiche: any = customer;
    let adresses: any[] = [];
    try {
      const [f, a]: any[] = await Promise.all([
        api.getCustomer(customer.id),
        api.get(`/customers/${customer.id}/addresses`),
      ]);
      fiche = f?.data ?? f ?? customer;
      adresses = (a?.data ?? a) || [];
    } catch {
      toast.error('Fiche du client illisible : réessayez dans un instant');
      return;
    }
    const principale = adresses.find((x) => x.is_default) ?? adresses[0] ?? null;
    setEditingCustomer(customer);
    setFormData({
      first_name: fiche.first_name,
      last_name: fiche.last_name,
      email: estEmailTechnique(fiche.email) ? '' : fiche.email,
      phone: fiche.phone || '',
      postal_address: fiche.postal_address || '',
      vat_number: fiche.vat_number || '',
    });
    setAdresseId(principale?.id ?? null);
    setAdresse(principale
      ? { rue: principale.street || '', cp: principale.postal_code || '', ville: principale.city || '' }
      : ADRESSE_VIDE);
    setTempPassword(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.first_name || !formData.last_name) {
      toast.error('Nom et prénom sont obligatoires');
      return;
    }
    // Sans email, le serveur pose une adresse technique (rien n'y part) ; en
    // modification, un champ vide garde l'email actuel.
    const { email, ...reste } = formData;
    const donnees = email.trim() ? { ...reste, email: email.trim() } : reste;
    const souci = verifierAdresse(adresse);
    if (souci) { toast.error(souci); return; }
    if (adresseRenseignee(adresse)) {
      donnees.postal_address = adresseEnUneLigne(adresse);
    } else if (editingCustomer) {
      // Trois champs vides en modification : on ne touche pas à l'adresse.
      delete (donnees as any).postal_address;
    }
    setSaving(true);
    try {
      if (editingCustomer) {
        await api.updateCustomer(editingCustomer.id, donnees);
        if (adresseRenseignee(adresse)) {
          const corps = corpsAdresse(adresse);
          await (adresseId
            ? api.patch(`/customers/${editingCustomer.id}/addresses/${adresseId}`, {
                street: corps.street, postal_code: corps.postal_code, city: corps.city, country: corps.country,
              })
            : api.post(`/customers/${editingCustomer.id}/addresses`, corps)
          ).catch(() => toast.warning('Fiche enregistrée, mais l’adresse n’a pas pu l’être.'));
        }
        toast.success('Client mis à jour');
        if (selectedCustomer?.id === editingCustomer.id) {
          loadCustomerDetail(editingCustomer.id);
        }
        // Enregistré : la fenêtre se ferme (elle restait ouverte, 13/10/2026).
        setShowModal(false);
      } else {
        const result = await api.createCustomer(donnees);
        if (adresseRenseignee(adresse)) {
          await api.post(`/customers/${result.id}/addresses`, corpsAdresse(adresse))
            .catch(() => toast.warning('Client créé, mais son adresse n’a pas pu être enregistrée : ajoutez-la depuis sa fiche.'));
        }
        // Le mot de passe temporaire ne sert qu'à un client qui a un email.
        const motDePasse = email.trim() ? result.temp_password || null : null;
        setTempPassword(motDePasse);
        if (!motDePasse) {
          toast.success('Client créé');
          setShowModal(false);
        }
      }
      loadCustomers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (customer: Customer) => {
    try {
      await api.updateCustomer(customer.id, { active: !customer.active });
      toast.success(customer.active ? 'Client désactivé' : 'Client réactivé');
      loadCustomers();
      if (selectedCustomer?.id === customer.id) {
        loadCustomerDetail(customer.id);
      }
    } catch (err) {
      toast.error('Erreur lors de la modification');
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Clients</h1>
          <p className="mt-1 text-gray-600">{total} client{total > 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors"
        >
          + Nouveau client
        </button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Rechercher par nom, email, téléphone..."
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        />
      </div>

      <div className="flex gap-6">
        {/* Customer list */}
        <div className={`${selectedCustomer ? 'w-1/2' : 'w-full'} transition-all`}>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Wallet</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto mb-2" />
                    Chargement...
                  </td></tr>
                ) : customers.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    {search ? `Aucun résultat pour "${search}"` : 'Aucun client'}
                  </td></tr>
                ) : (
                  customers.map((customer) => (
                    <tr
                      key={customer.id}
                      onClick={() => loadCustomerDetail(customer.id)}
                      className={`hover:bg-gray-50 cursor-pointer ${selectedCustomer?.id === customer.id ? 'bg-emerald-50' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{customer.first_name} {customer.last_name}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(customer.created_at).toLocaleDateString('fr-BE')}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-700">{estEmailTechnique(customer.email) ? '—' : customer.email}</div>
                        {customer.phone && <div className="text-xs text-gray-500">{customer.phone}</div>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-medium text-gray-900">
                          {formatPrice(customer.wallet?.balance_cents || customer.wallet_balance_cents || 0)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          customer.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {customer.active ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEdit(customer); }}
                          className="text-xs text-blue-600 hover:text-blue-800 mr-2"
                        >
                          Modifier
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleActive(customer); }}
                          className={`text-xs ${customer.active ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'}`}
                        >
                          {customer.active ? 'Désactiver' : 'Réactiver'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {!search && totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-30"
                >
                  Précédent
                </button>
                <span className="text-sm text-gray-600">Page {page} / {totalPages}</span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-30"
                >
                  Suivant
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Detail panel */}
        {selectedCustomer && (
          <div className="w-1/2">
            <div className="bg-white rounded-lg shadow p-6 sticky top-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedCustomer.first_name} {selectedCustomer.last_name}
                </h2>
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  x
                </button>
              </div>

              {detailLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Contact info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Contact</h3>
                    <div className="space-y-1">
                      <div className="text-sm"><span className="text-gray-500">Email :</span> {estEmailTechnique(selectedCustomer.email) ? 'aucun' : selectedCustomer.email}</div>
                      {selectedCustomer.phone && <div className="text-sm"><span className="text-gray-500">Tél :</span> {selectedCustomer.phone}</div>}
                      {selectedCustomer.postal_address && <div className="text-sm"><span className="text-gray-500">Adresse :</span> {selectedCustomer.postal_address}</div>}
                      {selectedCustomer.vat_number && <div className="text-sm"><span className="text-gray-500">TVA :</span> {selectedCustomer.vat_number}</div>}
                      <div className="text-sm">
                        <span className="text-gray-500">Email vérifié :</span>{' '}
                        <span className={selectedCustomer.email_verified ? 'text-green-600' : 'text-orange-600'}>
                          {selectedCustomer.email_verified ? 'Oui' : 'Non'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Paiement au retrait (19/10/2026) : chèques repas, espèces, Bancontact */}
                  <label className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 h-5 w-5 accent-amber-600"
                      checked={!!selectedCustomer.paiement_au_retrait}
                      onChange={async (e) => {
                        const autorise = e.target.checked;
                        try {
                          await api.definirPaiementAuRetrait(selectedCustomer.id, autorise);
                          setSelectedCustomer({ ...selectedCustomer, paiement_au_retrait: autorise });
                          toast.success(autorise ? 'Paiement au retrait autorisé' : 'Paiement au retrait retiré');
                        } catch {
                          toast.error('Modification impossible');
                        }
                      }}
                    />
                    <span>
                      <span className="block text-sm font-semibold text-amber-900">Peut payer au retrait</span>
                      <span className="block text-xs text-amber-800">
                        Sur la boutique en ligne, ce client pourra régler au comptoir (espèces, chèques repas, Bancontact) au lieu de payer d'avance.
                        Une commande non réglée bloque la marchandise jusqu'au retrait.
                      </span>
                    </span>
                  </label>

                  {/* Client du magasin ou nouveau (28/10/2026) : sert au relevé de rentabilité */}
                  <div className="rounded-lg border border-gray-200 p-4">
                    <span className="block text-sm font-semibold text-gray-900 mb-2">Ce client nous connaissait-il avant la boutique en ligne ?</span>
                    <div className="flex flex-wrap gap-2">
                      {([[true, 'Déjà client du magasin'], [false, 'Nouveau client'], [null, 'Je ne sais pas']] as const).map(([v, label]) => (
                        <button
                          key={String(v)}
                          type="button"
                          className={`px-3 py-2 rounded-lg text-sm border ${
                            (selectedCustomer.client_magasin ?? null) === v
                              ? 'bg-emerald-600 text-white border-emerald-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                          onClick={async () => {
                            try {
                              await api.definirClientMagasin(selectedCustomer.id, v);
                              setSelectedCustomer({ ...selectedCustomer, client_magasin: v });
                            } catch {
                              toast.error('Modification impossible');
                            }
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Wallet */}
                  <div className="bg-emerald-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-emerald-700 mb-2">Portefeuille</h3>
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-2xl font-bold text-emerald-800">
                        {formatPrice(selectedCustomer.wallet?.balance_cents || 0)}
                      </div>
                      <button
                        onClick={async () => {
                          const input = window.prompt('Montant à créditer (€) :');
                          if (!input) return;
                          const euros = parseFloat(input.replace(',', '.'));
                          if (isNaN(euros) || euros <= 0) {
                            toast.error('Montant invalide');
                            return;
                          }
                          try {
                            await api.creditCustomerWallet(selectedCustomer.id, Math.round(euros * 100));
                            toast.success(`Wallet crédité de ${euros.toFixed(2)} €`);
                            loadCustomerDetail(selectedCustomer.id);
                            loadCustomers();
                          } catch (err: any) {
                            toast.error(err.response?.data?.message || 'Erreur lors du crédit');
                          }
                        }}
                        className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 whitespace-nowrap"
                      >
                        + Créditer
                      </button>
                    </div>
                  </div>

                  {/* Addresses */}
                  {selectedCustomer.addresses && selectedCustomer.addresses.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Adresses ({selectedCustomer.addresses.length})</h3>
                      {selectedCustomer.addresses.map((addr: any) => (
                        <div key={addr.id} className="text-sm text-gray-700 mb-1">
                          {addr.is_default && <span className="text-xs bg-blue-100 text-blue-700 px-1 rounded mr-1">Par défaut</span>}
                          {addr.street}, {addr.postal_code} {addr.city}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Recent orders */}
                  {selectedCustomer.orders && selectedCustomer.orders.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Commandes récentes ({selectedCustomer.orders.length})</h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedCustomer.orders.slice(0, 10).map((order: any) => (
                          <div key={order.id} className="flex items-center justify-between text-sm">
                            <div>
                              <span className="font-medium">{order.order_number}</span>
                              <span className="text-gray-500 ml-2">{new Date(order.created_at).toLocaleDateString('fr-BE')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-1.5 py-0.5 text-xs rounded ${
                                order.status === 'completed' ? 'bg-green-100 text-green-700' :
                                order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {order.status}
                              </span>
                              <span className="font-medium">{formatPrice(order.total_cents || 0)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Preferences */}
                  {selectedCustomer.preferences?.notes && (
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-yellow-700 mb-1">Notes</h3>
                      <p className="text-sm text-gray-700">{selectedCustomer.preferences.notes}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(selectedCustomer)}
                      className="flex-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => handleToggleActive(selectedCustomer)}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg ${
                        selectedCustomer.active
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {selectedCustomer.active ? 'Désactiver' : 'Réactiver'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingCustomer ? 'Modifier le client' : 'Nouveau client'}
            </h2>

            {tempPassword ? (
              <div className="text-center">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-green-700 mb-2">Client créé avec succès !</p>
                  <p className="text-sm text-gray-600 mb-2">Mot de passe temporaire :</p>
                  <p className="text-lg font-mono font-bold text-gray-900 bg-white px-4 py-2 rounded border select-all">
                    {tempPassword}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">Communiquez ce mot de passe au client</p>
                </div>
                <button
                  onClick={() => { setShowModal(false); setTempPassword(null); }}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                >
                  Fermer
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="font-normal text-gray-500">(facultatif — laisser vide si le client n'en a pas)</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Adresse <span className="font-normal text-gray-500">(facultatif — utile pour une facture ou une livraison)</span>
                      </label>
                      <input
                        type="text"
                        value={adresse.rue}
                        onChange={(e) => setAdresse({ ...adresse, rue: e.target.value })}
                        placeholder="Rue et numéro"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={adresse.cp}
                          onChange={(e) => setAdresse({ ...adresse, cp: e.target.value })}
                          placeholder="Code postal"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                        <input
                          type="text"
                          value={adresse.ville}
                          onChange={(e) => setAdresse({ ...adresse, ville: e.target.value })}
                          placeholder="Ville"
                          className="col-span-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      {editingCustomer && !adresseId && formData.postal_address && (
                        <p className="mt-1 text-xs text-gray-500">
                          Adresse texte actuelle : {formData.postal_address} — gardée telle quelle si vous laissez ces champs vides.
                        </p>
                      )}
                    </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">N° TVA</label>
                    <input
                      type="text"
                      value={formData.vat_number}
                      onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                      placeholder="BE0123456789"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {saving ? 'Enregistrement...' : editingCustomer ? 'Enregistrer' : 'Créer'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
